import React, { useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "./Button";
import { Badge } from "./Badge";
import { hasClassificationProfile } from "../../utils/helpers";

// The "Classified" column filter of events, artists and venues (venues drop "No themes"). The last
// two are the candidates for Classify / Re-classify (#624), sent as the list's `classification_issue`.
export const CLASSIFIED_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "ambiguous", label: "Ambiguous" },
  { value: "no_themes", label: "No themes" },
];

// The list query params a `CLASSIFIED_FILTER_OPTIONS` value stands for.
export const classifiedFilterParams = (value) => {
  if (value === "yes") return { has_classification: true };
  if (value === "no") return { has_classification: false };
  return value ? { classification_issue: value } : {};
};

// Why a classification needs a look, if it does — the same tests as the API's
// `classification_issue_clauses`: `enricher` gave up on it, or its profile has no resolved theme.
// `classified` defaults to having a profile; a venue passes its own test and no profile (no themes).
const classificationIssue = (profile, meta, enricher, classified) => {
  if (!classified) {
    return meta?.ambiguous && meta.enricher === enricher ? "Ambiguous" : null;
  }
  if (!profile) return null;
  // A theme counts when it has an id — the filter's `exists themes_subjects.id`.
  const hasThemes = (profile.themes_subjects || []).some((theme) => theme?.id);
  // An event's inherited profile is fixed on its artist, as the events list's filter says.
  const inherited = enricher === "event_classification" && profile.classification_source === "artist";
  return hasThemes || inherited ? null : "No themes";
};

// The classification issue if there is one, else `labels` ([classified, unclassified]) or nothing.
export function ClassificationIssueBadge({ profile, meta, enricher, labels, classified = hasClassificationProfile(profile) }) {
  const issue = classificationIssue(profile, meta, enricher, classified);
  if (!issue) {
    if (!labels) return null;
    return classified ? (
      <Badge variant="secondary" className="text-xs">{labels[0]}</Badge>
    ) : (
      <Badge variant="outline" className="text-xs text-muted-foreground">{labels[1]}</Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-700 dark:text-amber-400">
      {issue}
    </Badge>
  );
}

// Busy state and result of the Classify / Re-classify button, keyed by entity id (#624). Held by
// the page rather than the modal: an admin can start one, open another entity and start that
// too, and each modal must show its own busy state and result — or a second paid click is one away.
export function useClassify(classify) {
  const [busyIds, setBusyIds] = useState({});
  const [results, setResults] = useState({});

  const run = async (id) => {
    setBusyIds((ids) => ({ ...ids, [id]: true }));
    setResults(({ [id]: _previous, ...rest }) => rest);
    let result;
    try {
      result = await classify(id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      result = {
        success: false,
        message: typeof detail === "string" ? detail : detail?.message || "Classification failed",
      };
    } finally {
      setBusyIds(({ [id]: _done, ...rest }) => rest);
    }
    setResults((all) => ({ ...all, [id]: result }));
    return result;
  };

  return { run, isBusy: (id) => Boolean(id && busyIds[id]), resultFor: (id) => (id ? results[id] : null) };
}

// The button, anything shown beside it (`children`), and the last result. `renderPrevious(result)`
// shows the classification a successful click replaced.
export function ClassifyAction({ busy, result, isReclassify, onClick, renderPrevious, children }) {
  const previous = result?.success && renderPrevious ? renderPrevious(result) : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onClick} disabled={busy}>
          {busy ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Classifying…</>
          ) : (
            <><RotateCcw className="h-4 w-4 mr-2" />{isReclassify ? "Re-classify" : "Classify"}</>
          )}
        </Button>
        {children}
      </div>
      {result && (
        <div
          className={`rounded-md border p-3 text-sm ${result.success ? "border-green-500/40 bg-green-500/10" : "border-amber-500/40 bg-amber-500/10"}`}
        >
          <p className="font-medium">{result.message}</p>
          {result.provider_errors?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              The request failed, not the act — provider error:{" "}
              {result.provider_errors.map((e) => [e.kind, e.status_code, e.message].filter(Boolean).join(" ")).join("; ")}
            </p>
          )}
          {result.estimated_cost_usd != null && (
            // Four places: one classification is a fraction of a cent.
            <p className="text-xs text-muted-foreground mt-1">Cost: ${Number(result.estimated_cost_usd).toFixed(4)}</p>
          )}
          {previous && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-medium">Previous classification</summary>
              <div className="mt-2">{previous}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
